import { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import {
  Plus,
  Search,
  Building2,
  Mail,
  Phone,
  User,
  Briefcase,
  Pencil,
  Trash2,
  Upload,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ContactoModal from "@/components/ContactoModal";

export default function Contactos({ user }) {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContacto, setSelectedContacto] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactoToDelete, setContactoToDelete] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchContactos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("q", search);
      params.append("limit", "100");

      const response = await api.get(`/contactos?${params.toString()}`);
      setContactos(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Error al cargar contactos");
      console.error("Error fetching contactos:", error);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContactos();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchContactos]);

  const handleDelete = async () => {
    if (!contactoToDelete) return;
    try {
      const id = contactoToDelete.contacto_id || contactoToDelete._id;
      await api.delete(`/contactos/${id}`);
      toast.success("Contacto eliminado");
      fetchContactos();
    } catch (error) {
      toast.error("Error al eliminar contacto");
    } finally {
      setDeleteDialogOpen(false);
      setContactoToDelete(null);
    }
  };

  const handleImportLeads = async () => {
    setImporting(true);
    try {
      const resp = await api.post('/admin/sync-leads-to-contactos', {});
      const { created, skipped, total_leads } = resp.data;
      toast.success(`Importados ${created} contactos de ${total_leads} leads (${skipped} omitidos)`);
      fetchContactos();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al importar";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleModalClose = (refresh) => {
    setModalOpen(false);
    setSelectedContacto(null);
    if (refresh) fetchContactos();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display theme-text">Contactos</h1>
          <p className="theme-text-secondary text-sm">
            {total} contacto{total !== 1 ? "s" : ""} registrado{total !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button
              onClick={handleImportLeads}
              disabled={importing}
              variant="outline"
              className="theme-text-secondary"
              style={{ borderColor: "var(--theme-border)" }}
            >
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Importar desde Leads
            </Button>
          )}
          <Button
            onClick={() => {
              setSelectedContacto(null);
              setModalOpen(true);
            }}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Contacto
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 theme-text-muted" />
        <Input
          placeholder="Buscar por empresa, nombre o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 theme-bg-secondary theme-text"
          style={{ borderColor: "var(--theme-border)" }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 theme-text-secondary animate-pulse">
          Cargando contactos...
        </div>
      ) : contactos.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto mb-3 theme-text-muted opacity-40" />
          <p className="theme-text-secondary">
            {search ? "No se encontraron contactos" : "Aun no hay contactos registrados"}
          </p>
          {!search && (
            <p className="text-sm theme-text-muted mt-1">
              Se crean automaticamente al convertir oportunidades o manualmente aqui
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ border: "1px solid var(--theme-border)" }}>
          <table className="w-full">
            <thead>
              <tr className="theme-bg-secondary">
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                  Empresa
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                  Contacto
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider hidden md:table-cell">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider hidden md:table-cell">
                  Telefono
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider hidden lg:table-cell">
                  Cargo
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider hidden lg:table-cell">
                  Sector
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold theme-text-secondary uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: "var(--theme-border)" }}>
              {contactos.map((c) => (
                <tr
                  key={c._id}
                  className="theme-bg hover:bg-cyan-400/5 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedContacto(c);
                    setModalOpen(true);
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="font-medium theme-text text-sm truncate max-w-[200px]">
                        {c.empresa}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 theme-text-muted flex-shrink-0" />
                      <span className="theme-text text-sm">{c.nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="theme-text-secondary text-sm">{c.email || "—"}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="theme-text-secondary text-sm">{c.telefono || "—"}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="theme-text-secondary text-sm">{c.cargo || "—"}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="theme-text-secondary text-sm">{c.sector || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedContacto(c);
                          setModalOpen(true);
                        }}
                        className="p-1.5 rounded-md hover:bg-cyan-400/10 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-3.5 h-3.5 text-cyan-400" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContactoToDelete(c);
                          setDeleteDialogOpen(true);
                        }}
                        className="p-1.5 rounded-md hover:bg-red-400/10 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <ContactoModal
        open={modalOpen}
        onClose={handleModalClose}
        contacto={selectedContacto}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="theme-bg-secondary" style={{ border: "1px solid var(--theme-border)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="theme-text">Eliminar contacto</AlertDialogTitle>
            <AlertDialogDescription className="theme-text-secondary">
              Se eliminara permanentemente a{" "}
              <strong>{contactoToDelete?.nombre}</strong> de{" "}
              <strong>{contactoToDelete?.empresa}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="theme-text-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
